<template>
  <div>
    <div class="el-input-group el-input-group--append el-input-group--prepend">
      <div class="medication-route-selects">
        <div class="medication-target-dropdown">
          <el-select
            v-close-on-zoom
            :value="state.target"
            :disabled="readonly"
            class="target-select"
            @input="value => updateTarget(value)"
          >
            <el-option value="ANY" :label="'Was ever'" />
            <el-option
              :disabled="state.selectedRoutes.length > 1"
              value="LATEST"
              :label="'Was last administration'"
            >
              Was last administration
              <help-tooltip
                v-if="state.selectedRoutes.length > 1"
                type="warning"
              >
                Only works with a single route selection
              </help-tooltip>
            </el-option>
            <el-option
              :disabled="state.selectedRoutes.length > 1"
              value="CUMULATIVE"
              :label="'Was cumulative administration'"
            >
              Was cumulative administration
              <help-tooltip
                v-if="state.selectedRoutes.length > 1"
                type="warning"
              >
                Only works with a single route selection
              </help-tooltip>
            </el-option>
            <el-option
              :disabled="state.selectedRoutes.length > 1"
              value="TRIGGERING"
              :label="'Was triggering administration'"
            >
              Was triggering administration
              <help-tooltip
                v-if="state.selectedRoutes.length > 1"
                type="warning"
              >
                Only works with a single route selection
              </help-tooltip>
            </el-option>
          </el-select>
        </div>
        <div class="container">
          <medication-fluid-selector
            class="medication-select"
            :class="{ error: !!medDisabledError }"
            :multiple="false"
            :show-fluids="false"
            :selected="state.selectedMedication"
            :case-conf="emitter.caseConf"
            :medications="medications"
            :disabled="readonly"
            :is-global="isGlobal"
            :dosage-form-filter="
              dosageForm =>
                dosageForm.enabledRoutes.find(r => r.key !== 'IV_IO_INFUSION')
            "
            @select="
              (treatmentType, treatmentIds) => {
                selectMedication(treatmentIds);
              }
            "
          />
          <help-tooltip v-if="medDisabledError" type="error" class="med-error-tooltip">
            <span>{{ medDisabledError.message }}<br></span>
          </help-tooltip>
          <help-tooltip v-if="routeNoneValidError" type="error" class="med-error-tooltip">
            <span>{{ routeNoneValidError.message }}<br></span>
          </help-tooltip>
        </div>
        <div class="container">
          <el-select
            id="medication-routes-select"
            v-close-on-zoom
            :value="state.selectedRoutes"
            placeholder="any route"
            :filterable="true"
            :multiple="true"
            :default-first-option="true"
            class="routes-select"
            :disabled="readonly"
            :class="{ error: routeDisabledError }"
            @input="routeKeys => selectRoutes(routeKeys)"
          >
            <el-option
              v-for="route in availableRoutes"
              :key="route.key"
              :label="route.name.en"
              :value="route.key"
            />
            <el-option
              v-for="route in unavailableSelectedRoutes"
              :key="route.key"
              :label="route.name.en"
              :value="route.key"
              :disabled="true"
            />
          </el-select>
          <help-tooltip
            v-if="routeDisabledError"
            type="error"
            class="route-error-tooltip"
          >
            <span>{{ routeDisabledError.message }}<br></span>
          </help-tooltip>
        </div>
        <div class="medication-metric-dropdown">
          <el-select
            v-close-on-zoom
            :value="state.metric"
            :disabled="readonly || !metricAvailable"
            class="metric-select"
            @input="value => updateMetric(value)"
          >
            <el-option value="GIVEN" :label="'given'" />
            <el-option value="DELIVERED" :label="'delivered'" />
          </el-select>
        </div>
      </div>
    </div>
    <div class="dosage-count-container">
      <dosage-count-check
        v-if="state.selectedMedication"
        :disabled="readonly"
        :mode="state.target === 'ANY' ? 'COUNT' : 'DOSAGE'"
        :prefix="state.target === 'ANY' ? 'with # of administrations' : 'with dosage between'"
        :dosage-available="dosageAvailable"
        :cumulative-available="false"
        :count-available="countAvailable"
        :dosage="state.dosage"
        :count="state.count"
        :count-operator="state.countOperator"
        :units="state.units"
        :units-available="availableUnits"
        :count-warning="countWarning"
        class="dosage-count-check"
        @updateDosage="updateDosage"
        @updateCountOperator="updateCountOperator"
        @updateCount="updateCount"
        @updateUnits="updateUnits"
      />
      <help-tooltip
        v-if="state.target !== 'ANY' && state.selectedRoutes.length === 0"
        type="warning"
      >
        You need to select one route in order to check the dosage
      </help-tooltip>
      <help-tooltip v-if="unitDisabledError" type="error">
        <span>{{ unitDisabledError.message }}</span>
      </help-tooltip>
      <help-tooltip v-if="dosageUnitError" type="error">
        <span>{{ dosageUnitError.message }}</span>
      </help-tooltip>
    </div>
    <el-card v-if="ivPushLocationAvailable || pushTimeframesAvailable">
      <div slot="header" class="clearfix">
        <span>IV Push Options</span>
        <el-button
          style="float: right; padding: 3px 0"
          type="text"
          @click="clearIvOptions()"
        >
          Clear
        </el-button>
      </div>
      <div class="iv-options-card-body">
        <div class="push-timeframe-location-select-container">
          <el-select
            v-if="pushTimeframesAvailable"
            v-close-on-zoom
            :value="state.pushTimeframes || []"
            :disabled="readonly"
            multiple
            placeholder="Any push timeframe"
            class="push-timeframe-select"
            @input="value => updatePushTimeframes(value)"
          >
            <el-option
              v-for="timeframe in availablePushTimeframes"
              :key="timeframe"
              :value="timeframe"
              :label="pushTimeframeLabel(timeframe)"
            />
          </el-select>
          <el-select
            v-if="ivPushLocationAvailable"
            v-close-on-zoom
            :value="state.ivPushLocation"
            :disabled="readonly"
            placeholder="Push location"
            class="iv-push-location-select"
            @input="value => updateIvPushLocation(value)"
          >
            <el-option value="IV_SITE" label="IV Site" />
            <el-option value="MEDICATION" label="Medication" />
            <el-option value="FLUID" label="Fluid" />
          </el-select>
        </div>
        <div class="push-location-options-container">
          <iv-location-select
            v-if="ivPushLocationAvailable && state.ivPushLocation === 'IV_SITE'"
            :iv-location="state.ivPushLocationIv || {}"
            :case-conf="emitter.caseConf"
            :is-global="isGlobal"
            :readonly="readonly"
            @update="updateIvPushLocationIv"
          />

          <medication-fluid-selector
            v-if="ivPushLocationAvailable && state.ivPushLocation === 'MEDICATION'"
            class="medication-select"
            :multiple="true"
            :show-fluids="false"
            :selected="state.ivPushLocationMedications || []"
            :case-conf="emitter.caseConf"
            :medications="medications"
            :disabled="readonly"
            :is-global="isGlobal"
            :dosage-form-filter="
              dosageForm =>
                dosageForm.enabledRoutes.find(r => r.key === 'IV_IO_INFUSION')
            "
            @select="
              (treatmentType, treatmentIds) => {
                updateIvPushLocationMedications(treatmentIds);
              }
            "
          />
          <help-tooltip
            v-if="ivPushLocationAvailable && state.ivPushLocation === 'MEDICATION' && locationMedErrors.length"
            type="error"
          >
            <span v-for="(error, idx) in locationMedErrors" :key="'med-error-' + idx">
              {{ error.message }}
              <br>
            </span>
          </help-tooltip>

          <medication-fluid-selector
            v-if="ivPushLocationAvailable && state.ivPushLocation === 'FLUID'"
            :multiple="true"
            :show-meds="false"
            :selected="state.ivPushLocationFluids || []"
            :case-conf="emitter.caseConf"
            :disabled="readonly"
            :is-global="isGlobal"
            @select="
              (treatmentType, treatmentIds) => {
                updateIvPushLocationFluids(treatmentIds);
              }
            "
          />
        </div>
        <help-tooltip
          v-if="ivPushLocationAvailable && state.ivPushLocation === 'FLUID' && locationFluidErrors.length"
          type="error"
        >
          <span v-for="(error, idx) in locationFluidErrors" :key="'fluid-error-' + idx">
            {{ error.message }}
            <br>
          </span>
        </help-tooltip>
      </div>
    </el-card>
  </div>
</template>

<script>
import get from 'lodash/get';
import isNumber from 'lodash/isNumber';
import keyBy from 'lodash/keyBy';
import orderBy from 'lodash/orderBy';
import uniqBy from 'lodash/uniqBy';
import HelpTooltip from '~lib/common/components/HelpTooltip';
import defaultRoutes from '~lib/common/config/routes';

import DosageCountCheck from './common/DosageCountCheck';
import IVLocationSelect from './common/IVLocationSelect';
import MedicationFluidSelector from './common/MedicationFluidSelector';

import { pushTimeFrames, unitTypes } from '~lib/common/config/medication';
import medicationCheckMixins from './common/MedicationCheckMixins';

export default {
  name: 'medication-given-check',
  components: {
    DosageCountCheck,
    HelpTooltip,
    'iv-location-select': IVLocationSelect,
    MedicationFluidSelector,
  },
  mixins: [ medicationCheckMixins ],
  props: [
    'medications',
    'readonly',
    'emitter',
    'ikey',
    'getData',
    'putData',
    'control',
  ],
  data() {
    return {
      state: {
        selectedMedication: null,
        selectedRoutes: [],
        dosage: { min: null, max: null },
        count: null,
        countOperator: 'EQUAL',
        units: null,
        triggeringAction: false,
        target: 'ANY',
        metric: null,
        pushTimeframes: null,
        ivPushLocation: null,
        ivPushLocationIv: null,
        ivPushLocationMedications: null,
        ivPushLocationFluids: null,
      },
      value: 0,
    };
  },
  computed: {
    isGlobal() {
      return this.emitter.isGlobal;
    },
    allMedications() {
      const medications = this.medications || [];
      const customMedications = this.emitter.caseConf.customMedications || [];
      return uniqBy([...medications, ...customMedications], 'id');
    },
    allMedicationsById() {
      return keyBy(this.allMedications, 'id');
    },
    availableMedicationsById() {
      return keyBy(
        this.availableMedications.map(m => ({
          ...m,
          routes: orderBy(m.routes, 'name.en'),
        })),
        'id',
      );
    },
    dosageAvailable() {
      return this.state.target !== 'ANY' && this.isSingleRoute;
    },
    hasDosage() {
      return this.state.dosage.min !== null || this.state.dosage.max !== null;
    },
    isSingleRoute() {
      return (
        this.state.selectedRoutes && this.state.selectedRoutes.length === 1
      );
    },
    countAvailable() {
      return this.state.target === 'ANY';
    },
    metricAvailable() {
      return (
        this.state.target !== 'ANY' &&
        this.state.selectedRoutes &&
        this.state.selectedRoutes.length === 1 &&
        this.state.selectedRoutes[0] === 'IV_IO_PUSH'
      );
    },
    ivPushLocationAvailable() {
      return (
        this.state.selectedRoutes &&
        this.state.selectedRoutes.length === 1 &&
        this.state.selectedRoutes[0] === 'IV_IO_PUSH'
      );
    },
    pushTimeframesAvailable() {
      return (
        this.state.selectedRoutes &&
        this.state.selectedRoutes.length === 1 &&
        this.state.selectedRoutes[0] === 'IV_IO_PUSH'
      );
    },
    availableMedications() {
      const { isGlobal } = this.emitter;
      if (isGlobal) {
        return this.medications || [];
      }
      const dosageForms = this.emitter.caseConf.dosageForms || [];
      return dosageForms
        .filter(df => {
          return df.enabledRoutes.find(route => route.key != 'IV_IO_INFUSION') ||
                 df.enabledRoutes.length === 0
        })
        .map(df => {
          const med = this.allMedications.find(med => med.id === df.medicationId)
          return {
            ...df,
            id: med.id,
            name: med.name,
            routes: med.routes,
          };
        });
    },
    availableMedicationsWithInfusionRoute() {
      const dosageForms = this.emitter.caseConf.dosageForms || [];
      return dosageForms
        .filter(df =>
          df.enabledRoutes.find(r => r.key === 'IV_IO_INFUSION'),
        )
        .map(
          df => this.allMedicationsById[df.medicationId],
        );
    },
    availableRoutes() {
      if (
        !this.state.selectedMedication ||
        !this.availableMedicationsById[this.state.selectedMedication]
      ) {
        return [];
      }
      const { id } = this.availableMedicationsById[
        this.state.selectedMedication
      ];

      const { isGlobal } = this.emitter;
      if (isGlobal) {
        return defaultRoutes.filter(route => route.key !== 'IV_IO_INFUSION');
      }
      const { caseConf } = this.emitter;
      if (!caseConf.dosageForms) {
        return [];
      }
      const caseMedication = caseConf.dosageForms.find(df => df.medicationId === id);
      if (!caseMedication) {
        return [];
      }
      const { enabledRoutes } = caseMedication;
      const results = defaultRoutes
        .filter(route => route.key !== 'IV_IO_INFUSION')
        .filter(defaultRoute =>
          enabledRoutes.some(
            enabledRoute => defaultRoute.key === enabledRoute.key,
          ),
        );
      return results;
    },
    unavailableSelectedRoutes() {
      return defaultRoutes.filter(
        route =>
          !this.availableRoutes.includes(route) &&
          this.state.selectedRoutes.includes(route.key),
      );
    },
    selectedMedication() {
      if (!this.state.selectedMedication) {
        return null;
      }
      return this.allMedicationsById[this.state.selectedMedication];
    },
    availableUnits() {
      if (!this.state.selectedMedication) {
        return [];
      }
      if (this.state.selectedRoutes.length !== 1) {
        return [];
      }
      const { isGlobal } = this.emitter;
      if (isGlobal) {
        return unitTypes.map(unitType => ({
          label: unitType.label.en,
          value: unitType.key
        }));
      }

      const caseDosageForm = (this.emitter.caseConf.dosageForms || []).find(
        df => df.medicationId === this.state.selectedMedication,
      );
      if (!caseDosageForm) {
        return [];
      }
      let units = [];

      for (const value of caseDosageForm.administeringUnits) {
        const unit = unitTypes.find(u => u.key === value);
        if (unit) {
          units.push({
            label: unit.label.en,
            value: unit.key
          });
        }
      }
      return units;
    },
    availablePushTimeframes() {
      if (this.isGlobal) {
        return pushTimeFrames.map(tf => tf.key);
      }

      const caseDosageForm = (this.emitter.caseConf.dosageForms || []).find(
        df => df.medicationId === this.state.selectedMedication,
      );
      if (!caseDosageForm) {
        return [];
      }
      const ivIoPushEnabledRoute = caseDosageForm.enabledRoutes.find(
        enabledRoute => enabledRoute.key === 'IV_IO_PUSH',
      );
      if (!ivIoPushEnabledRoute) {
        return [];
      }
      return ivIoPushEnabledRoute.options.pushTimeframes || [];
    },
    countWarning() {
      return this.warnings.find(warning => warning.metadata.countWarning);
    },
  },
  watch: {
    dosageAvailable() {
      if (!this.dosageAvailable) {
        this.resetDosage();
        this.state.target = 'ANY';
      }
    },
    countAvailable() {
      if (!this.countAvailable) {
        this.resetCount();
      }
    },
    metricAvailable() {
      if (!this.metricAvailable) {
        this.resetMetric();
      }
    },
    pushTimeframesAvailable() {
      if (!this.pushTimeframesAvailable) {
        this.resetPushTimeframes();
      }
    },
    ivPushLocationAvailable() {
      if (!this.ivPushLocationAvailable) {
        this.resetIvPushLocation();
      }
    },
  },
  beforeMount() {
    const data = this.getData(this.ikey);
    this.state.selectedMedication = get(data, 'id');
    this.state.selectedRoutes = get(data, 'routes') || [];
    this.state.dosage = get(data, 'dosage') || { min: null, max: null };
    this.state.count = isNumber(get(data, 'count')) ? get(data, 'count') : null;
    this.state.countOperator = get(data, 'countOperator') || 'EQUAL';
    this.state.units = get(data, 'units') || null;
    this.state.triggeringAction = get(data, 'triggeringAction') || false;
    this.state.target = get(data, 'target') || 'ANY';
    this.state.metric = get(data, 'metric') || 'GIVEN';
    this.state.pushTimeframes = get(data, 'pushTimeframes') || null;
    this.state.ivPushLocation = get(data, 'ivPushLocation') || null;
    this.state.ivPushLocationIv = get(data, 'ivPushLocationIv') || null;
    this.state.ivPushLocationMedications = get(data, 'ivPushLocationMedications') || null;
    this.state.ivPushLocationFluids = get(data, 'ivPushLocationFluids') || null;
    this.emitter.trigger('process', this.control);
  },
  methods: {
    selectMedication(medicationId) {
      this.state.selectedMedication = medicationId;
      this.state.selectedRoutes = this.state.selectedRoutes.filter(a =>
        this.availableRoutes.some(b => a === b.key),
      );
      this.update();
    },
    selectRoutes(routeKeys) {
      this.state.selectedRoutes = routeKeys;
      this.state.units = null;
      this.update();
    },
    updateDosage(updates) {
      this.state.dosage = {
        ...this.state.dosage,
        ...updates,
      };
      this.update();
    },
    resetDosage() {
      this.updateDosage({ min: null, max: null });
    },
    updateCount(count) {
      this.state.count = count;
      this.update();
    },
    resetCount() {
      this.state.count = null;
      this.state.countOperator = null;
      this.update();
    },
    updateCountOperator(op) {
      this.state.countOperator = op;
      this.update();
    },
    updateUnits(units) {
      this.state.units = units;
      this.update();
    },
    updateTarget(value) {
      this.state.target = value;
      this.update();
    },
    updateMetric(value) {
      this.state.metric = value;
      this.update();
    },
    resetMetric() {
      this.state.metric = 'GIVEN';
    },
    updatePushTimeframes(value) {
      this.state.pushTimeframes = value;
      this.update();
    },
    updateIvPushLocation(value) {
      this.state.ivPushLocation = value;
      if (this.state.ivPushLocation !== 'IV_SITE') {
        this.resetIvPushLocationIv();
      }
      if (this.state.ivPushLocation !== 'MEDICATION') {
        this.resetIvPushLocationMedications();
      }
      if (this.state.ivPushLocation !== 'FLUID') {
        this.resetIvPushLocationFluids();
      }
      this.update();
    },
    resetIvPushLocation() {
      this.state.ivPushLocation = null;
      this.resetIvPushLocationIv();
      this.resetIvPushLocationMedications();
      this.resetIvPushLocationFluids();
      this.update();
    },
    resetPushTimeframes() {
      this.state.pushTimeframes = [];
    },
    updateIvPushLocationIv(value) {
      this.state.ivPushLocationIv = value;
      this.update();
    },
    resetIvPushLocationIv() {
      this.state.ivPushLocationIv = null;
    },
    updateIvPushLocationMedications(value) {
      this.state.ivPushLocationMedications = value;
      this.update();
    },
    resetIvPushLocationMedications() {
      this.state.ivPushLocationMedications = null;
      this.update();
    },
    updateIvPushLocationFluids(fluidIds) {
      this.state.ivPushLocationFluids = fluidIds;
      this.update();
    },
    resetIvPushLocationFluids() {
      this.state.ivPushLocationFluids = null;
      this.update();
    },
    clearIvOptions() {
      this.resetIvPushLocation();
      this.resetPushTimeframes();
      this.resetIvPushLocationIv();
      this.resetIvPushLocationMedications();
      this.resetIvPushLocationFluids();
    },
    pushTimeframeLabel(selectedPushTimeframe) {
      const pushTimeframe = pushTimeFrames.find(pushTimeframe => pushTimeframe.key === selectedPushTimeframe);
      if (!pushTimeframe) {
        return 'N/A'
      }
      return pushTimeframe.label.en;
    },
    update() {
      this.putData(this.ikey, {
        type: 'MEDICATION_ADMINISTERED',
        id: this.state.selectedMedication,
        routes: this.state.selectedRoutes,
        dosage: this.state.dosage,
        count: this.state.count,
        countOperator: this.state.countOperator,
        operator: 'ADMINISTERED',
        value: true,
        units: this.state.units,
        target: this.state.target,
        metric: this.state.metric,
        pushTimeframes: this.state.pushTimeframes,
        ivPushLocation: this.state.ivPushLocation,
        ivPushLocationIv: this.state.ivPushLocationIv,
        ivPushLocationMedications: this.state.ivPushLocationMedications,
        ivPushLocationFluids: this.state.ivPushLocationFluids,
      });
      this.emitter.trigger('process', this.control);
    },
  },
};
</script>

<style scoped>
.medication-route-selects {
  display: flex;
}
.medication-target-dropdown {
  display: flex;
  flex: 0.8;
}
.el-select {
  flex: 1;
}
.el-select.error {
  border: solid 3px #f92c2d;
}
.med-error-tooltip,
.route-error-tooltip {
  position: absolute;
  right: 8px;
  top: 8px;
}
.container {
  display: flex;
  position: relative;
  flex: 1;
}

.bottom-row {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
}

.med-admin-types,
.med-triggering-action {
  flex: 1;
}

.med-admin-types-content,
.med-triggering-action-content {
  display: flex;
  align-items: center;
  padding: 5px;
}

.med-admin-types .md-checkbox,
.med-triggering-action .md-checkbox {
  margin: 0;
  margin-left: 5px;
}

.med-admin-types .md-checkbox >>> .md-checkbox-label {
  padding-left: 5px;
}

.dosage-count-container {
  display: flex;
  align-items: center;
}

.dosage-count-check {
  flex: 1;
}

.push-timeframe-location-select-container {
  display: flex;
  flex-direction: row;
  align-items: center;
}

.push-location-options-container {
  margin-top: 10px;
  display: flex;
  flex-direction: row-reverse;
}
</style>
